import * as React from "react";
import { useTranslation } from "react-i18next";

import { LinkTo } from "@/components/icons/link-to";
import { Card } from "@/components/shared/card";
import useAnalytics from "@/hooks/use-analytics";

import { Button } from "../button";
import { Link } from "../link";

export type DataExistsCardProps = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  importText: string;
  buttonText: string;
  onClickImport: () => void;
  href: string;
};

export default function DataExistsCard(props: DataExistsCardProps) {
  const linkRef = React.useRef<HTMLAnchorElement | null>(null);
  const { logAnalytics } = useAnalytics();
  const { t } = useTranslation();

  React.useEffect(() => {
    function handleOpenDashboardClick() {
      logAnalytics("CANVAS_EDITOR_PREFERENCE_PANEL_BUTTON", {
        button_text: "open_in_dashboard",
      });
    }

    const button = linkRef.current;

    if (button) {
      button.addEventListener("click", handleOpenDashboardClick);
    }

    return () => {
      if (button) {
        button.removeEventListener("click", handleOpenDashboardClick);
      }
    };
  }, [logAnalytics]);

  return (
    <Card data-testid="data-exists-card">
      <div className="flex flex-col items-center">
        <props.icon
          className="size-6 text-blue-500"
          data-testid="data-exists-card-icon"
        />
        {props.importText && (
          <p className="my-4 text-center text-sm font-medium leading-6 text-gray-800">
            {props.importText}
          </p>
        )}
        {props.buttonText && (
          <Button
            size="small"
            className="mb-4 h-auto w-max max-w-full whitespace-normal break-words px-7 py-1"
            onClick={props.onClickImport}
            data-testid="import-type-button"
          >
            {props.buttonText}
          </Button>
        )}
        <Link
          href={props.href}
          ref={linkRef}
          target="_blank"
          rel="noreferrer noopener"
          variant="outlined"
          size="small"
          className="border-solid"
          data-testid="open-dashboard-button"
        >
          {t("preference-panel.edit-in-dashboard")}
          <LinkTo className="ml-4 size-6 text-blue-400" />
        </Link>
      </div>
    </Card>
  );
}
